import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FormikHelpers, FormikTouched, FormikErrors, Field, FieldProps, FieldArray, Formik, Form } from 'formik';
import { withRouter } from 'react-router';
import { fromGlobalId } from 'graphql-relay';
import { useMutation, useQuery } from '@apollo/client/react/hooks';
import Styled from 'styled';
import { RouteComponentProps } from 'react-router';
import { AlertContext } from 'lib/alert-context/alert-context';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import { insertEmojiToText } from 'four-nets/ui-v2/emoji-selector/insert-emoji-to-text';

import ImageInput from 'four-nets/ui-v2/form/image-input/image-input';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import IconSmile from 'four-nets/icons/svg/icon_smile.svg';
import SelectionItems from 'four-nets/photoblog/my-selection/selection-items/selection-items';
import EmojiSelectorContainer from 'four-nets/ui-v2/emoji-selector/emoji-selector-container';
import PhotoFileUpload from 'four-nets/photoblog/posts/album/detail/photo-file-upload';
import Avatar from 'four-nets/avatar/avatar';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';

import DateSelect from 'four-nets/user/settings/date-select/date-select';
import GeoSelect from 'four-nets/geo/geo-select/geo-select';
import Input from 'four-nets/ui-v2/form/input/input';

import createPostMutation, { CreatePostMutation, CreatePostMutationVariables } from './mutations/create-post.graphql';
import { PostVisibility, PostType, PostCategory } from './mutations/create-post.graphql.types';
import PostTypeHeader from './post-type-header/post-type-header';
import SubmitButton from './submit-button/submit-button';
import VisibilitySelect from './visibility-select/visibility-select';
import weddingDateQuery, { WeddingDateQuery } from './wedding-date.graphql';

import defaultStyles from './create.scss';
import ShortMessageImageUpload from './short-message-image-upload/short-message-image-upload';

export interface Props extends RouteComponentProps {
    styles: typeof defaultStyles;
    placeholder?: string;
    hideReply?: () => void;
    refetch?: () => Promise<any> | any;
    refetchQueries?: any;
    groupId?: string;
}

export interface Values {
    title: string;
    visibility: PostVisibility;
    content?: string;
    photofileUploads: PhotoFileUpload[];

    brideName: string;
    groomName: string;
    weddingCounty: string | null;
    weddingCountry: string | null;
    weddingCity: string;
    day: string;
    month: string;
    year: string;
}

const Create: React.FC<Props> = (props, { authCheck } = {}) => {
    const { styles, refetchQueries, groupId } = props;

    const { acknowledge, retry } = useContext(AlertContext);
    const { viewer } = useContext(ViewerContext);

    const imperativeFormikHandle = useRef<((name: string, value: any) => void) | null>(null);
    const { data } = useQuery<WeddingDateQuery>(weddingDateQuery, {
        onCompleted: result => {
            if (result.viewer?.wedding?.weddingDate && imperativeFormikHandle.current) {
                const date = new Date(result.viewer.wedding.weddingDate);
                const day = date.getDate();
                const month = date.getMonth().toString();
                const year = date.getFullYear().toString();

                imperativeFormikHandle.current('day', day);
                imperativeFormikHandle.current('month', month);
                imperativeFormikHandle.current('year', year);
            }
        },
    });

    const [createPost] = useMutation<CreatePostMutation, CreatePostMutationVariables>(createPostMutation, {
        refetchQueries,
    });

    const [inputKey, setInputKey] = useState(0);
    const [focused, setFocused] = useState(false);

    const [postType, setPostType] = useState('POST_SHORT_MESSAGE');
    const [postCategory, setPostCategory] = useState<PostCategory | undefined>(undefined);
    const [selectionItems, setSelectionItems] = useState(new Set<string>());
    const [emojiSelection, setEmojiSelection] = useState(false);
    const [textArea, setTextArea] = useState({ textAreaHeight: 35, textAreaWidth: 0 });

    const textRef = useRef<HTMLTextAreaElement>(null);
    const divRef = useRef<HTMLDivElement>(null);

    const validate = (values: Values) => {
        const errors: any = {};

        if (postCategory === 'WEDDING_ALBUM') {
            const brideName = mergeValidators([emptyValidator])(values.brideName);
            const groomName = mergeValidators([emptyValidator])(values.groomName);
            const weddingCounty = mergeValidators([emptyValidator])(values.weddingCounty);
            const weddingCountry = mergeValidators([emptyValidator])(values.weddingCountry);
            const day = mergeValidators([emptyValidator])(values.day);
            const month = mergeValidators([emptyValidator])(values.month);
            const year = mergeValidators([emptyValidator])(values.year);

            if (weddingCounty && weddingCountry) {
                errors.weddingCounty = weddingCounty;
                errors.weddingCountry = weddingCountry;
            }

            const weddingErrors = { ...errors, brideName, groomName, day, month, year };
            Object.keys(weddingErrors).forEach(key =>
                weddingErrors[key] === undefined ? delete weddingErrors[key] : {}
            );
            return weddingErrors;
        }

        const titleErrors = mergeValidators([emptyValidator])(values.title);

        if (titleErrors && !values.photofileUploads.length) {
            errors.title = titleErrors;
        }

        values.photofileUploads.forEach((value, i) => {
            if (!value.isValid()) {
                errors[`photofileUploads[${i}]`] = true;
            }
        });

        return errors;
    };

    const focus = () => {
        setFocused(true);
    };

    const blur = () => {
        setFocused(false);
    };

    const onSelectionItemsChange = (selectionItems: Set<string>) => {
        setSelectionItems(selectionItems);
    };

    const onTextAreaFocus = () => {
        if (authCheck) {
            authCheck(Promise.resolve, AuthOrigin.NewBlogPost);
        }
    };

    const textField = (
        touched: FormikTouched<Values>,
        errors: FormikErrors<Values>,
        handleChange: (e: React.ChangeEvent<any>) => void
    ) => {
        const { styles, placeholder } = props;
        const textFieldFormik = (
            <React.Fragment>
                <div style={{ width: textArea.textAreaWidth }} className={styles.textareaDiv} ref={divRef} />
                <label htmlFor='title-field-id' className={styles.titleLabel}>
                    {placeholder}
                </label>
                <Field
                    id='title-field-id'
                    name='title'
                    style={{ height: textArea.textAreaHeight }}
                    innerRef={textRef}
                    placeholder={!focused && postType === 'POST_SHORT_MESSAGE' ? placeholder : ''}
                    component='textarea'
                    onChange={(event: React.ChangeEvent) => {
                        setEmojiSelection(false);
                        handleChange(event);
                    }}
                    onFocus={onTextAreaFocus}
                    validate={handleResize} // here because it needs to be executed after handleChange
                    className={classnames(styles.textarea, {
                        [styles.album]: postType === 'POST_ALBUM',
                        [styles.invalid]: touched.title && errors.title && focused,
                    })}
                />
            </React.Fragment>
        );

        if (postCategory === 'WEDDING_ALBUM') {
            return null;
        }

        return postType === 'POST_ALBUM' ? (
            <label className={styles.labelWrapper}>
                <span className={styles.label}>
                    <FormattedMessage id='ALBUM_NAME' />
                </span>
                {textFieldFormik}
            </label>
        ) : (
            textFieldFormik
        );
    };

    const handleResize = () => {
        if (divRef && divRef.current && textRef && textRef.current) {
            divRef.current.innerText = textRef.current.textContent || '';

            setTextArea({
                textAreaHeight: divRef.current.clientHeight,
                textAreaWidth: textRef.current.clientWidth,
            });
        }
    };

    const onClickAway = () => {
        setEmojiSelection(false);
    };

    const onEmojiSelect = (setFieldValue: FormikHelpers<Values>['setFieldValue']) => (_: any, emoji: string) => {
        if (!textRef.current) {
            return;
        }

        const { selectionStart } = textRef.current;
        const [newValue, insertedLen] = insertEmojiToText(textRef.current.value, emoji, selectionStart);
        const newCursorPosition = selectionStart + (insertedLen as number);

        setFieldValue('title', newValue, false);
        textRef.current.focus();

        // textarea sets cursor at end when set without timeout
        setTimeout(() => textRef.current!.setSelectionRange(newCursorPosition, newCursorPosition), 0);
    };

    const toggleEmojiSelector = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setEmojiSelection(!emojiSelection);
        if (textRef && textRef.current) {
            textRef.current.focus();
        }
    };

    return (
        <div className={styles.container}>
            <HideForUnpaidVendor>
                <div
                    className={classnames(styles.wrapper, {
                        [styles.focused]: focused,
                    })}
                    tabIndex={0}
                    onFocus={focus}
                    onBlur={blur}
                >
                    {viewer && !groupId ? (
                        <PostTypeHeader
                            postType={postType}
                            postCategory={postCategory}
                            setTypeAndCategory={(postType: PostType, postCategory?: PostCategory) => {
                                if (postType === 'POST_ARTICLE') {
                                    props.history.push(`/blog/${viewer.username}/add/article/`);
                                } else if (postCategory === 'WEDDING_ALBUM' && viewer.weddingAlbumHref) {
                                    props.history.push(viewer.weddingAlbumHref);
                                } else {
                                    setPostCategory(postCategory);
                                    setPostType(postType);
                                    setFocused(true);
                                }
                            }}
                        />
                    ) : null}
                    <Formik<Values>
                        initialValues={{
                            title: '',
                            visibility: 'POST_VISIBILITY_PUBLIC',
                            photofileUploads: [],
                            weddingCountry: '',
                            weddingCounty: '',
                            weddingCity: '',
                            brideName: '',
                            groomName: '',
                            day: '0',
                            month: '0',
                            year: String(new Date().getFullYear()),
                        }}
                        validate={validate}
                        onSubmit={(values, actions) => {
                            if (authCheck) {
                                authCheck(() => {
                                    const onAccept = () => {
                                        actions.setSubmitting(true);

                                        const { photofileUploads, day, month, year, ...rest } = values;

                                        let photoFileIds: number[] = [];
                                        if (postType === 'POST_SHORT_MESSAGE') {
                                            photoFileIds = photofileUploads.map(({ photofile }) =>
                                                parseInt(fromGlobalId(photofile!.id).id, 10)
                                            );
                                        }

                                        const weddingDate = new Date();
                                        weddingDate.setDate(Number(day));
                                        weddingDate.setMonth(Number(month));
                                        weddingDate.setFullYear(Number(year));

                                        return createPost({
                                            variables: {
                                                input: {
                                                    ...rest,
                                                    postType,
                                                    postCategory,
                                                    photoFileIds,
                                                    selectionItemIds: Array.from(selectionItems),
                                                    group: props.groupId,
                                                    weddingDate: weddingDate.toISOString(),
                                                },
                                                hasGroup: Boolean(props.groupId),
                                            },
                                        })
                                            .then(result => {
                                                if (postType === 'POST_ALBUM' && result) {
                                                    const username = result!.data!.createPost!.post!.user!.username;
                                                    const slug = result.data!.createPost!.post!.slug;
                                                    const path = `/blog/${username}/album/${slug}/`;
                                                    window.location.href = path;
                                                } else {
                                                    actions.resetForm();
                                                    if (props.refetch) {
                                                        props.refetch();
                                                    }
                                                    setFocused(false);
                                                    setPostType('POST_SHORT_MESSAGE');
                                                    setEmojiSelection(false);
                                                }
                                            })
                                            .catch((error: any) => {
                                                retry!({
                                                    body: error.message,
                                                    onAccept,
                                                });
                                            })
                                            .finally(() => {
                                                actions.setSubmitting(false);
                                            });
                                    };

                                    return onAccept();
                                }, AuthOrigin.NewBlogPost).finally(() => {
                                    actions.setSubmitting(false);
                                });
                            }
                        }}
                    >
                        {({
                            isSubmitting,
                            submitForm,
                            isValid,
                            values,
                            validateForm,
                            setFieldValue,
                            handleChange,
                            touched,
                            errors,
                            getFieldProps,
                        }) => {
                            imperativeFormikHandle.current = setFieldValue;

                            return (
                                <Form
                                    className={classnames(styles.withFocus, {
                                        [styles.show]: isValid,
                                    })}
                                >
                                    <div
                                        tabIndex={0}
                                        className={classnames(styles.contentWrapper, {
                                            [styles.contentWrapperAlbum]: postType === 'POST_ALBUM',
                                        })}
                                    >
                                        <Avatar style={{ avatar: styles.avatar }} user={viewer} />
                                        <div className={styles.inputWrapper}>
                                            {textField(touched, errors, handleChange)}
                                            {postType === 'POST_SHORT_MESSAGE' ? (
                                                <FieldArray name='photofileUploads'>
                                                    {helpers => (
                                                        <React.Fragment>
                                                            <div className={styles.additionalInputWrapper}>
                                                                {emojiSelection ? (
                                                                    <EmojiSelectorContainer
                                                                        onClickAway={onClickAway}
                                                                        onEmojiSelect={onEmojiSelect(setFieldValue)}
                                                                    />
                                                                ) : null}
                                                                <div className={styles.additionalInput}>
                                                                    <button
                                                                        aria-label='Emoji button'
                                                                        type='button'
                                                                        className={styles.emojiButton}
                                                                        onClick={toggleEmojiSelector}
                                                                    >
                                                                        <SVGIcon
                                                                            className={styles.iconSmile}
                                                                            src={IconSmile}
                                                                        />
                                                                    </button>
                                                                    <ImageInput
                                                                        inputKey={inputKey}
                                                                        multiple={true}
                                                                        onClick={event => {
                                                                            if (!viewer) {
                                                                                event.preventDefault();
                                                                                if (authCheck) {
                                                                                    authCheck();
                                                                                }
                                                                            }
                                                                        }}
                                                                        onChange={event => {
                                                                            const duplicates: File[] = [];
                                                                            const next = [...values.photofileUploads];

                                                                            for (const file of Array.from(
                                                                                event.target.files || []
                                                                            )) {
                                                                                let duplicate;

                                                                                for (const {
                                                                                    file: previous,
                                                                                } of values.photofileUploads) {
                                                                                    if (
                                                                                        previous.name === file.name &&
                                                                                        previous.size === file.size &&
                                                                                        previous.lastModified ===
                                                                                            file.lastModified &&
                                                                                        previous.type === file.type
                                                                                    ) {
                                                                                        duplicate = file;
                                                                                        break;
                                                                                    }
                                                                                }

                                                                                if (duplicate) {
                                                                                    duplicates.push(duplicate);
                                                                                } else {
                                                                                    next.push(
                                                                                        new PhotoFileUpload(file)
                                                                                    );
                                                                                }
                                                                            }

                                                                            setFieldValue(
                                                                                'photofileUploads',
                                                                                next,
                                                                                true
                                                                            );

                                                                            if (duplicates.length) {
                                                                                const messages = duplicates.map(
                                                                                    (duplicate, i) => (
                                                                                        <React.Fragment>
                                                                                            <FormattedMessage
                                                                                                id='IMAGE%(image)s_ALREADY_IN_THIS_ALBUM'
                                                                                                values={{
                                                                                                    image: duplicate.name,
                                                                                                }}
                                                                                            />
                                                                                            {i !==
                                                                                            duplicates.length - 1 ? (
                                                                                                <br />
                                                                                            ) : null}
                                                                                        </React.Fragment>
                                                                                    )
                                                                                );

                                                                                acknowledge!({
                                                                                    body: (
                                                                                        <React.Fragment>
                                                                                            {messages}
                                                                                        </React.Fragment>
                                                                                    ),
                                                                                });
                                                                            }

                                                                            setInputKey(inputKey + 1);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className={styles.collection}>
                                                                <ShortMessageImageUpload
                                                                    photofileUploads={values.photofileUploads}
                                                                    helpers={helpers}
                                                                    validateForm={validateForm}
                                                                    onCompleted={focus}
                                                                    // tslint:disable-next-line: no-empty
                                                                    onProgress={() => {}}
                                                                />
                                                            </div>
                                                        </React.Fragment>
                                                    )}
                                                </FieldArray>
                                            ) : null}
                                            {postType === 'POST_ALBUM' && postCategory !== 'WEDDING_ALBUM' ? (
                                                <React.Fragment>
                                                    <Field name='content'>
                                                        {({ field }: FieldProps<Values>) => (
                                                            <React.Fragment>
                                                                <label className={styles.labelWrapper}>
                                                                    <span
                                                                        style={{
                                                                            marginTop: 10,
                                                                        }}
                                                                        className={styles.label}
                                                                    >
                                                                        <FormattedMessage id='ALBUM_DESCRIPTION' />
                                                                    </span>
                                                                    <textarea
                                                                        className={classnames(
                                                                            styles.textarea,
                                                                            styles.album
                                                                        )}
                                                                        {...(field as any)}
                                                                    />
                                                                </label>
                                                            </React.Fragment>
                                                        )}
                                                    </Field>
                                                    <span
                                                        style={{
                                                            marginTop: 10,
                                                        }}
                                                        className={styles.label}
                                                    >
                                                        <FormattedMessage id='ALBUM_CATEGORY' />
                                                    </span>
                                                    <SelectionItems onChange={onSelectionItemsChange} />
                                                </React.Fragment>
                                            ) : null}
                                            {postType === 'POST_ALBUM' && postCategory === 'WEDDING_ALBUM' ? (
                                                <>
                                                    <Field name='brideName'>
                                                        {({ field }: FieldProps) => (
                                                            <label>
                                                                <FormattedMessage id='BRIDE'>
                                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                                </FormattedMessage>
                                                                <Input
                                                                    errors={
                                                                        errors.brideName
                                                                            ? (errors.brideName as any)
                                                                            : undefined
                                                                    }
                                                                    touched={touched.brideName}
                                                                    {...field}
                                                                />
                                                            </label>
                                                        )}
                                                    </Field>
                                                    <Field name='groomName'>
                                                        {({ field }: FieldProps) => (
                                                            <label className={styles.weddingAlbumField}>
                                                                <FormattedMessage id='GROOM'>
                                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                                </FormattedMessage>
                                                                <Input
                                                                    errors={
                                                                        errors.groomName
                                                                            ? (errors.groomName as any)
                                                                            : undefined
                                                                    }
                                                                    touched={touched.groomName}
                                                                    {...field}
                                                                />
                                                            </label>
                                                        )}
                                                    </Field>
                                                    <label className={styles.weddingAlbumField}>
                                                        <FormattedMessage id='WEDDING_DATE'>
                                                            {msg => <span className={styles.label}>{msg}</span>}
                                                        </FormattedMessage>
                                                        <DateSelect
                                                            style={{ label: styles.dateLabel }}
                                                            yearRange={{ min: 1980, max: new Date().getFullYear() + 5 }}
                                                            dayField={getFieldProps('day')}
                                                            monthField={getFieldProps('month')}
                                                            yearField={getFieldProps('year')}
                                                        />
                                                    </label>
                                                    <label className={styles.weddingAlbumField}>
                                                        <FormattedMessage id='WEDDING_LOCATION'>
                                                            {msg => <span className={styles.label}>{msg}</span>}
                                                        </FormattedMessage>
                                                        <GeoSelect
                                                            style={{ wrapper: styles.geoSelectWrapper }}
                                                            {...getFieldProps('weddingCounty')}
                                                            countyId={values.weddingCounty}
                                                            countryId={values.weddingCountry}
                                                            allowCountrySelection={true}
                                                            onChange={(selectedCountyID, selectedCountryID) => {
                                                                setFieldValue('weddingCounty', selectedCountyID);
                                                                setFieldValue('weddingCountry', selectedCountryID);
                                                            }}
                                                        />
                                                        {!values.weddingCounty &&
                                                        !values.weddingCountry &&
                                                        (errors.weddingCounty || errors.weddingCountry) ? (
                                                            <p className={styles.error}>
                                                                {errors.weddingCounty || errors.weddingCountry}
                                                            </p>
                                                        ) : null}
                                                    </label>
                                                    <Field name='weddingCity'>
                                                        {({ field }: FieldProps) => (
                                                            <label className={styles.weddingAlbumField}>
                                                                <FormattedMessage id='CITY_MUNICIPALITY'>
                                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                                </FormattedMessage>
                                                                <Input
                                                                    errors={
                                                                        errors.weddingCity
                                                                            ? (errors.weddingCity as any)
                                                                            : undefined
                                                                    }
                                                                    touched={touched.weddingCity}
                                                                    {...field}
                                                                />
                                                            </label>
                                                        )}
                                                    </Field>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.footerPlaceholder} />
                                    <div tabIndex={0} className={styles.footer}>
                                        {!groupId ? (
                                            <Field name='visibility'>
                                                {({ field }: FieldProps<Values>) => (
                                                    <VisibilitySelect {...(field as any)} />
                                                )}
                                            </Field>
                                        ) : null}

                                        <SubmitButton
                                            postType={postType}
                                            disabled={!isValid || isSubmitting}
                                            onClick={submitForm}
                                        />
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </HideForUnpaidVendor>
        </div>
    );
};

Create.contextTypes = {
    authCheck: PropTypes.func,
};

export default Styled(defaultStyles)(withRouter(Create));
